<template>
  <div>
    <loading-spinner />
    <loading-customer-spinner />
    <div v-if="audience && !loadingStatus">
      <ul class="nav nav-tabs">
        <li class="nav-item">
          <a
            :class="{active: tab==='summary'}"
            @click="openTab('summary')"
          >Summary</a>
        </li>
        <li class="nav-item">
          <span class="divider">|</span>
        </li>
        <li class="nav-item">
          <a
            id="customerDetails"
            :class="{active: tab==='customer-details'}"
            @click="openTab('customer-details')"
          >Customer Details</a>
        </li>
      </ul>
      <div
        v-show="tab==='summary'"
        id="summary"
      >
        <div class="row">
          <div class="col-sm-9">
            <h2 v-if="audienceDetails">
              {{ audienceDetails.audienceName }}
            </h2>
            <h2 v-if="!audienceDetails">
              Audience Not Set
            </h2>
          </div>
          <div
            v-if="!audienceDetails"
            class="col-sm-3"
          >
            <div class="float-right">
              <ejs-tooltip
                ref="newAudienceTooltip"
                :content="newAudienceTemplate"
                css-class="customtip"
                position="BottomCenter"
                :offset-x="-20"
                opens-on="Click"
              >
                <button class="btn btn-lg btn-primary">
                  Save Audience
                </button>
              </ejs-tooltip>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-3 col-md-6">
            <info-card
              :title="audienceDetails ? numFormat(audienceDetails.totalRecords) : '-'"
              description="Total Records"
              icon="users"
              icon-color="gray"
            />
          </div>
          <div class="col-lg-3 col-md-6">
            <info-card
              :title="audienceDetails ? numFormat(audienceDetails.campaignsUsing) : '-'"
              description="Campaigns Using"
              icon="envelope"
              icon-color="gray"
            />
          </div>
          <div class="col-lg-3 col-md-6">
            <info-card
              v-if="audienceDetails"
              :title="dateTimeWithTimezone(audienceDetails.lastSaved)"
              description="Last Saved"
              icon="save"
              icon-color="gray"
            />
            <info-card
              v-if="!audienceDetails"
              title="-"
              description="Last Saved"
              icon="save"
              icon-color="gray"
            />
          </div>
          <div class="col-lg-3 col-md-6">
            <info-card
              v-if="audienceDetails"
              :title="dateTimeWithTimezone(audienceDetails.lastPublished)"
              description="Last Published"
              icon="arrow-from-bottom"
              icon-color="gray"
            />
            <info-card
              v-if="!audienceDetails"
              title="-"
              description="Last Published"
              icon="arrow-from-bottom"
              icon-color="gray"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6">
            <Card
              v-show="criteriaData"
              title="Criteria"
            >
              <Criteria
                ref="criteriaRef"
                :key="criteriaComponentKey"
                :criteria-data="criteriaData"
                :filters="audience.filters"
              />
            </Card>
          </div>
          <div class="col-sm-6">
            <Card
              v-if="audience.projectCounts"
              title="Channel Counts"
            >
              <div v-if="!countGenerating">
                <ejs-accordion>
                  <e-accordionitems>
                    <e-accordionitem
                      :header="emailHeader"
                      :content="emailTemplate"
                    />
                  </e-accordionitems>
                </ejs-accordion>
                <table class="table-list table-list-lines border-top">
                  <tr>
                    <td>Total Records</td>
                    <td>{{ totalRecords }}<span class="spacing" /></td>
                  </tr>
                </table>
                <small>Counts may vary from customer list results due to your property access.</small>
              </div>
              <div
                v-if="countGenerating"
                class="text-center py-3"
              >
                <div>
                  <div class="my-2">
                    <font-awesome-icon
                      :icon="['fal', 'sync']"
                      size="4x"
                      class="icon--gray"
                    />
                  </div>
                </div>
                <div>
                  Generating Counts
                </div>
              </div>
            </Card>
            <Card
              v-if="audience.properties"
              title="Properties"
            >
              <div>
                <p
                  v-for="(property, index) in audience.properties"
                  :key="index"
                >
                  {{ property.name }}
                </p>
              </div>
            </Card>
            <Card title="General Information">
              <table>
                <tr>
                  <td>Tags</td>
                  <td>{{ audienceDetails ? audienceDetails.tags.join(',') : '-' }}</td>
                </tr>
                <tr>
                  <td>Description</td>
                  <td>{{ audienceDetails ? audienceDetails.description : '-' }}</td>
                </tr>
              </table>
            </Card>
          </div>
        </div>
      </div>
      <div
        v-show="tab==='customer-details'"
        id="customer-details"
      >
        <div class="row">
          <div class="col-sm-9">
            <h2 v-if="audienceDetails">
              {{ audienceDetails.audienceName }}
            </h2>
            <h2 v-if="!audienceDetails">
              Audience Not Set
            </h2>
          </div>
          <div class="col-sm-3">
            <div class="float-right">
              <button
                class="btn btn-lg btn-outline-secondary"
                @click="exportCustomers"
              >
                Export
              </button>
            </div>
          </div>
        </div>
        <div class="row mx-0">
          <ejs-grid
            ref="grid"
            class="cendyn-grid sortingenabled"
            :allow-paging="true"
            :allow-filtering="true"
            :page-settings="pageSettings"
            :filter-settings="filterOptions"
            :allow-sorting="true"
            :data-source="customers"
            :data-bound="dataBound"
            :data-state-change="dataStateChange"
            :action-begin="actionBegin"
            :action-complete="actionComplete"
            clip-mode="EllipsisWithTooltip"
          >
            <e-columns>
              <e-column
                field="firstname"
                header-text="First Name"
              />
              <e-column
                field="lastname"
                header-text="Last Name"
              />
              <e-column
                field="email"
                header-text="Email"
              />
              <e-column
                field="emailStatus"
                header-text="Email Status"
                :value-accessor="emailStatusAccess"
                :filter="emailFilter"
              />
              <e-column
                field="reservation"
                header-text="Reservation"
              />
              <e-column
                field="zipCode"
                header-text="Zip"
              />
              <e-column
                field="property"
                header-text="Property"
              />
              <e-column
                field="source"
                header-text="Source"
              />
              <e-column
                field=" "
                header-text=""
                :template="customerActionTemplate"
                :allow-filtering="false"
                width="40"
              />
            </e-columns>
          </ejs-grid>
        </div>
      </div>
      <ejs-toast
        ref="errorConvertingRef"
        title="Error"
        content="There is some error converting to audience"
        :position="toastPosition"
        css-class="notification-error"
      />
      <ejs-toast
        ref="exportCustomersRef"
        title="Customer Detail Export"
        content="Your report is being generated and will be sent to your email"
        :position="toastPosition"
      />
      <ejs-toast
        ref="exportCustomersErrorRef"
        title="Customer Detail Export"
        content="Export failed."
        css-class="notification-error"
        :position="toastPosition"
      />
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import InfoCard from '../components/InfoCard'
import Card from '../components/Card'
import Criteria from '../components/Criteria'
import LoadingSpinner from '../components/LoadingSpinner'
import LoadingCustomerSpinner from '../components/LoadingCustomerSpinner'
import GridCustomerAction from '../components/Grid/GridCustomerAction'
import { phraseStyles } from '../mixins/phraseStyles'
import { Page, Sort, Filter, GridPlugin } from '@syncfusion/ej2-vue-grids'
import { MultiSelect, CheckBoxSelection } from '@syncfusion/ej2-dropdowns'
import { createElement } from '@syncfusion/ej2-base'
import { mapState, mapGetters } from 'vuex'
import numeral from 'numeral'
import moment from 'moment'
import { iframeParentCommunication } from '../mixins/iframeParentCommunication'
import { systemTimezone } from '../constants/general'

MultiSelect.Inject(CheckBoxSelection)
Vue.use(GridPlugin)

const emailTemplate = function () {
  return {
    template: Vue.component('EmailTemplate', {
      data () {
        return {
          projectCount: {}
        }
      },
      mounted () {
        const audience = this.$store.getters.getCampaignAudience
        this.projectCount = audience.projectCounts.find(x => x.name === 'Email')
        this.colorFilteredColumns()
      },
      template: `<table class="table-list table-list-lines" v-if="projectCount">
        <template v-if=projectCount.counts>
          <tr class="border-bottom">
            <td v-if="projectCount.counts.CountInvalid || projectCount.counts.CountInvalid === 0">Null</td>
            <td v-if="!projectCount.counts.CountInvalid && projectCount.counts.CountInvalid !== 0">Null or Invalid</td>
            <td>{{ projectCount.counts.CountNull | numFormat }}</td>
          </tr>
          <tr v-if="projectCount.counts.CountInvalid || projectCount.counts.CountInvalid === 0" class="border-bottom">
            <td>Invalid</td>
            <td>{{ projectCount.counts.CountInvalid | numFormat }}</td>
          </tr>
          <tr class="border-bottom">
            <td>Unsubscribed</td>
            <td>{{ projectCount.counts.CountUnsubscribed | numFormat }}</td>
          </tr>
          <tr class="border-bottom">
            <td>Bounced</td>
            <td>{{ projectCount.counts.CountBounced | numFormat }}</td>
          </tr>
          <tr class="border-bottom">
            <td>Non-Consent</td>
            <td>{{ projectCount.counts.CountNonConsent | numFormat }}</td>
          </tr>
          <tr class="border-bottom">
            <td>Flagged</td>
            <td>{{ projectCount.counts.CountFlagged | numFormat }}</td>
          </tr>
          <tr class="border-bottom">
            <td>Valid</td>
            <td>{{ projectCount.counts.CountValid | numFormat }}</td>
          </tr>
          <tr>
            <td>Unique</td>
            <td>{{ projectCount.counts.CountUnique | numFormat }}</td>
          </tr>
        </template>
        <template v-if="!projectCount.counts">
          <tr class="border-bottom">
            <td>Null or Invalid</td>
            <td>-</td>
          </tr>
          <tr class="border-bottom">
            <td>Unsubscribed</td>
            <td>-</td>
          </tr>
          <tr class="border-bottom">
            <td>Bounced</td>
            <td>-</td>
          </tr>
          <tr class="border-bottom">
            <td>Non-Consent</td>
            <td>-</td>
          </tr>
          <tr class="border-bottom">
            <td>Flagged</td>
            <td>-</td>
          </tr>
          <tr class="border-bottom">
            <td>Valid</td>
            <td>-</td>
          </tr>
          <tr>
            <td>Unique</td>
            <td>-</td>
          </tr>
        </template>
      </table>`
    })
  }
}
const newAudienceTemplate = function (data) {
  return {
    template: Vue.component('NewAudienceTemplate', {
      data () {
        return {
          audienceName: '',
          hasError: false
        }
      },
      methods: {
        closeTooltipButton () {
          this.$parent.$parent.closeTooltipButton()
        },
        convertToAudience () {
          if (this.audienceName !== '' && this.audienceName.length < 255) {
            this.$parent.$parent.convertToAudience(this.audienceName)
          } else {
            this.hasError = true
          }
        }
      },
      template: `<div ref="newAudienceTemplate">
          <form id="new-audience-form">
            <div class="form-group">
              <label for="audienceName">Audience Name</label>
              <input type="text" class="form-control" id="audienceName" aria-describedby="" v-model="audienceName" v-bind:style="hasError ? 'border-color:#A82823;' : 'border-color:#ccc;'">
              <span v-if="audienceName.length > 255">Audience name exceeded 255 characters</span>
            </div>
            <div class="text-right">
              <button type="button" class="btn btn-lg btn-outline-secondary" @click='closeTooltipButton'>Cancel</button>
              <button type="button" class="btn btn-lg btn-primary ml-2" @click='convertToAudience'>Save</button>
            </div>
          </form>
        </div>`
    })
  }
}

export default {
  name: 'CampaignDetailsAudience',
  components: {
    InfoCard,
    Card,
    Criteria,
    LoadingSpinner,
    LoadingCustomerSpinner
  },
  mixins: [phraseStyles, iframeParentCommunication],
  provide: {
    grid: [Page, Sort, Filter]
  },
  data () {
    return {
      timezoneEnv: '',
      tab: 'summary',
      pageSettings: { pageCount: 5, pageSize: 10, pageSizes: ['10', '25', '50', '100'] },
      filterOptions: {
        type: 'Menu',
        mode: 'OnEnter',
        operators: {
          stringOperator: [
            { value: 'startsWith', text: 'Starts With' },
            { value: 'endsWith', text: 'Ends With' },
            { value: 'contains', text: 'Contains' },
            { value: 'equal', text: 'Equal' }
          ]
        }
      },
      emailFilter: {
        ui: {
          create: (args) => {
            const flValInput = createElement('input', { className: 'flm-input' })
            args.target.appendChild(flValInput)
            if (args.target.parentElement.children) {
              args.target.parentElement.children[0].style.display = 'none'
              args.target.parentElement.children[1].style.paddingTop = '0px'
            }
            this.dropInstance = new MultiSelect({
              dataSource: [
                { emailStatus: 'Valid' },
                { emailStatus: 'Invalid' },
                { emailStatus: 'Null' },
                { emailStatus: 'Unsubscribed' },
                { emailStatus: 'Bounced' },
                { emailStatus: 'Non-Consent' },
                { emailStatus: 'Flagged' }
              ],
              fields: { text: 'emailStatus', value: 'emailStatus' },
              placeholder: 'Select a value',
              popupHeight: '200px',
              mode: 'CheckBox'
            })
            this.dropInstance.appendTo(flValInput)
          },
          write: (args) => {
            let arr = []
            if (args.parent.filterModule.actualPredicate.emailStatus !== undefined) {
              arr = args.parent.filterModule.actualPredicate.emailStatus.map(element => element.value)
            }
            this.dropInstance.value = Array.isArray(args.filteredValue)
              ? args.filteredValue
              : arr
          },
          read: (args) => {
            args.fltrObj.filterSettings.columns = args.fltrObj.filterSettings.columns.filter(x => x.field !== args.column.field)
            args.fltrObj.filterByColumn(
              args.column.field,
              'equal',
              this.dropInstance.value
            )
          }
        }
      },
      emailTemplate: emailTemplate,
      newAudienceTemplate: newAudienceTemplate,
      toastPosition: { X: 'Right', Y: 'Top' },
      exportCustomersContent: '',
      exportCustomerClass: '',
      filters: [],
      gridInitLoad: true,
      criteriaComponentKey: 0,
      gridLoaded: false,
      generatingCountsTimeout: null,
      customerActionTemplate () {
        return {
          template: {
            extends: GridCustomerAction
          }
        }
      }
    }
  },
  computed: {
    ...mapState({
      campaign: state => state.manageCampaigns.campaign,
      audience: state => state.manageCampaigns.campaign.audience,
      countGenerating: state => state.manageCampaigns.campaign.countGenerating,
      customers: state => state.manageCampaigns.campaign.customers,
      dynamicQuery: state => state.manageCampaigns.campaign.dynamicQuery,
      audienceDetails: state => state.manageCampaigns.campaign.audience.audienceDetail,
      emailHeader: state => {
        const emailCounts = state.manageCampaigns.campaign.audience.projectCounts.find(x => x.name === 'Email')
        const countTotal = emailCounts.counts ? emailCounts.records.toLocaleString() : '-'
        return `<span>Email</span><span class='float-right'>${countTotal}</span>`
      },
      totalRecords: state => {
        const emailCounts = state.manageCampaigns.campaign.audience.projectCounts.find(x => x.name === 'Email')
        const countTotal = emailCounts.counts ? emailCounts.records.toLocaleString() : '-'
        return countTotal
      },
      criteriaData: state => {
        const audience = state.manageCampaigns.campaign.audience
        if (audience.audienceDetail && audience.audienceDetail.criteria) {
          return audience.audienceDetail.criteria.designModel
        } else if (audience.projectCriteria) {
          return audience.projectCriteria.designModel
        }
        return null
      }
    }),
    ...mapGetters({
      loadingStatus: 'getLoadingStatus'
    })
  },
  watch: {
    countGenerating (isCountGenerating) {
      if (isCountGenerating && !this.generatingCountsTimeout) {
        const parentCompanyId = this.$route.query.clientId
        const projectId = this.$route.query.campaignId
        const userEmail = this.$route.query.userEmail ? atob(this.$route.query.userEmail) : null
        this.generatingCountsTimeout = setInterval(() => {
          this.$store.dispatch('setCountGenerating', { parentCompanyId, projectId, userEmail })
        }, 3000)
      }
      if (!isCountGenerating && this.generatingCountsTimeout) {
        clearInterval(this.generatingCountsTimeout)
      }
    }
  },
  mounted () {
    if (this.$route.query.tab === 'customer-details') {
      this.tab = 'customer-details'
    }
    const parentCompanyId = this.$route.query.clientId
    const projectId = this.$route.query.campaignId
    const userEmail = this.$route.query.userEmail ? atob(this.$route.query.userEmail) : null

    if (parentCompanyId && projectId && userEmail) {
      if (!this.campaign.summaryDetails.projectId) {
        this.$store.dispatch('setCampaignSummary', { projectId, userEmail })
      }
      this.$store.dispatch('setCampaignAudience', { parentCompanyId, projectId, userEmail })

      this.loadCustomers()
      const criteriaSection = this.$refs.criteriaRef
      if (criteriaSection) {
        criteriaSection.loadFilters().then(() => {
          this.criteriaComponentKey += 1 // re-rendering Criteria
        })
      }
    }
    this.timezoneEnv = systemTimezone()
  },
  methods: {
    closeTooltipButton () {
      this.$refs.newAudienceTooltip.close()
    },
    convertToAudience (audienceName) {
      const parentCompanyId = this.$route.query.clientId
      const projectId = this.$route.query.campaignId
      const userEmail = this.$route.query.userEmail ? atob(this.$route.query.userEmail) : null
      this.$store.dispatch('setCampaignConvertToAudience', { projectId, audienceName, userEmail })
        .then(response => {
          console.log('CampaignDetailsAudience setCampaignConvertToAudience: ', response.data.status)
          console.log('CampaignDetailsAudience setCampaignConvertToAudience: ', response)
          if (response.data.status === 200) {
            this.$store.commit('UPDATE_CAMPAIGNS_AUDIENCE', { response: response.data, projectId })
            if (parentCompanyId && projectId && userEmail) {
              this.$store.dispatch('setCampaignAudience', { parentCompanyId, projectId, userEmail })
              this.$store.dispatch('setCampaignAudienceCustomers', { parentCompanyId, projectId, userEmail })
            }
          } else {
            console.log('error converting')
            this.$refs.errorConvertingRef.show()
          }
        }, error => {
          console.log(error)
          console.log('error converting')
          this.$refs.errorConvertingRef.show()
        })
    },
    emailStatusAccess: (field, data, column) => {
      if (data.emailStatus === 'Valid Email') {
        data.emailStatus = 'Valid'
      } else if (data.emailStatus === 'Invalid Email') {
        data.emailStatus = 'Invalid'
      } else if (data.emailStatus === 'Bounced Email') {
        data.emailStatus = 'Bounced'
      }
      return data.emailStatus
    },
    numFormat: (value, format = '0,0') => {
      return numeral(value).format(format)
    },
    openTab (tab) {
      const query = this.$route.query
      if (!query.tab || query.tab !== tab) {
        this.tab = tab
        this.$router.replace({ query: { ...query, tab } })
      }
      if (!this.gridLoaded) {
        this.loadCustomers()
      }
    },
    loadCustomers () {
      const state = {
        action: {
          currentPage: 1
        },
        skip: 0,
        take: 10
      }
      this.dataStateChange(state)
    },
    dataStateChange (dataState) {
      this.$refs.grid.hideSpinner()
      const parentCompanyId = this.$route.query.clientId
      const projectId = this.$route.query.campaignId
      const userEmail = this.$route.query.userEmail ? atob(this.$route.query.userEmail) : null
      if (dataState.action.requestType !== 'stringfilterrequest') {
        this.$store.dispatch('setCampaignAudienceCustomers', { parentCompanyId, projectId, userEmail, dataState })
          .then(response => {
            this.gridLoaded = true
          })
      }
    },
    dataBound () {
      this.$refs.grid.hideSpinner()
      this.formatPagination()
    },
    exportCustomers () {
      const projectId = this.$route.query.campaignId
      const userEmail = this.$route.query.userEmail ? atob(this.$route.query.userEmail) : null
      const dynamicQuery = this.dynamicQuery
      this.$store.dispatch('exportCustomers', { projectId, userEmail, dynamicQuery })
        .then(response => {
          // this.$refs.exportCustomersRef.show()
          const data = {
            action: 'eIn_show_alert',
            value: {
              headline: 'Customer Detail Export',
              message: 'Your report is being generated and will be sent to your email',
              type: 'success'
            }
          }
          this.SendMessageToParent(data)
        }, error => {
          console.log(error)
          // this.$refs.exportCustomersErrorRef.show()
          const data = {
            action: 'eIn_show_alert',
            value: {
              headline: 'Customer Detail Export',
              message: 'Export failed.',
              type: 'danger'
            }
          }
          this.SendMessageToParent(data)
        })
    },
    colorFilteredColumns () {
      this.filters = this.$refs.grid.ej2Instances.filterSettings.columns
      if (this.filters) {
        const that = this
        this.$refs.grid.ej2Instances.columns = this.$refs.grid.ej2Instances.columns.map(column => {
          if (that.filters.find(filter => filter.field === column.field)) {
            column.customAttributes = { class: 'filtered' }
          } else if (column.customAttributes) {
            delete column.customAttributes
          }
          return column
        })
      }
    },
    formatPagination () {
      // Timeout is to avoid conflict with the component re-rendering
      setTimeout(() => {
        const pageNo = document.getElementsByClassName('e-pagenomsg')
        const pageCount = document.getElementsByClassName('e-pagecountmsg')
        if (pageNo[0] && pageCount[0]) {
          pageNo[0].innerHTML = this.numberFormat(pageNo[0].innerHTML)
          pageCount[0].innerHTML = this.numberFormat(pageCount[0].innerHTML)
        }
      }, 10)
    },
    actionBegin: function (args) {
      if (args.requestType === 'filtering' || args.requestType === 'refresh' || args.requestType === 'paging') {
        this.formatPagination()
      }
    },
    actionComplete: function (args) {
      if (args.action === 'clearFilter' || args.action === 'clear-filter' || args.action === 'filter') {
        this.colorFilteredColumns()
      }
    },
    dateTimeWithTimezone (date) {
      return date ? `${moment(date).format('L LT')} (${this.timezoneEnv})` : '-'
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "../../node_modules/@syncfusion/ej2-vue-grids/styles/bootstrap4.css";
  @import '../../node_modules/@syncfusion/ej2-base/styles/bootstrap4.css';
  @import '../../node_modules/@syncfusion/ej2-buttons/styles/bootstrap4.css';
  @import '../../node_modules/@syncfusion/ej2-calendars/styles/bootstrap4.css';
  @import '../../node_modules/@syncfusion/ej2-dropdowns/styles/bootstrap4.css';
  @import '../../node_modules/@syncfusion/ej2-inputs/styles/bootstrap4.css';
  @import '../../node_modules/@syncfusion/ej2-navigations/styles/bootstrap4.css';
  @import '../../node_modules/@syncfusion/ej2-popups/styles/bootstrap4.css';
  @import '../../node_modules/@syncfusion/ej2-splitbuttons/styles/bootstrap4.css';
  @import '../../node_modules/@syncfusion/ej2-vue-grids/styles/bootstrap4.css';

  .nav-tabs {
    border-bottom: none;
  }

  .nav-item {
    margin-bottom: 20px;

    .divider {
      margin: 0 8px;
    }

    a {
      color: $blue-dark;
      text-decoration: none;

      &:hover {
        cursor: pointer;
      }

      &.active {
        text-decoration: underline;
      }
    }
  }

  .spacing {
    margin-right: 28px;
  }

  small, .small {
    font-size: 1.4rem;
  }

  .icon--gray {
    color: #999;
  }
</style>
